import _ from 'lodash';

import BudgetCalcRulesEvaluator from 'common/requestDetails/prepFire/BudgetCalcRulesEvaluator';
import CostItemObjectTotaler from 'common/requestDetails/prepFire/CostItemObjectTotaler';
import {
  safeAdd,
  safeAddNoFixed,
  safeMult,
  safeDivide,
  safeSubstractNoFixed,
  safeSubstract,
} from 'common/utils';
/*
 * The use of safeAddNoFixed and safeSubstractNoFixed [sic] were originally used to keeps some accuracy,
 * but for some particular arguments that only had 2 decimal places (e.g., period total and period appShare)
 * would result in a value that had more than 2 decimal places (e.g., safeAddNoFixed(8.22, 8.3) = 16.520000000000003).
 * An updated version of those in the legacy (non-TDR 2.1) was used to update the TDR version being used
 * here which makes use of bignumber and converts back to number. But to be sure there remains only
 * 2-digit precision the parseFloat(moneyVal.toFixed(2)) is used in certain places before results are returned,
 * and that rounds down/up to that hundredths place.
 */

/**
 * GMMGO-67661 PrepFire refactor budget calculations epic
 *
 * This class encapsulates the centralized logic to be used to determine cost share values through PrepFire
 * grants life cycle.
 *
 * This class computes the cost share information that goes into the payload.budget.share object.  Specifically,
 * it calculates the appShare, fedShare, appRate, and fedRate properties.  See NOTE below regarding rate properties.
 * There is an extras object that by default will equal {isEligibable: true, message:{}}.  The AFG and FPS programs
 * are configured with a fedShareLimit value.  After initial calculation of appShare and fedShare, if fedShare is
 * exceeded, the fedShare gets capped at the fedShareLimit value and the excess dollars are added to period values
 * in appShare.  In such a case the extras object will contain a message indicating the limit, provided such a
 * message was configured.
 *
 * Another message that has been configured in the past for AFG funding opportunities (FOs) is
 * "Not eligible for current AFG funding" for applicants from MH or FM (Marshal Islands or Federated States of
 * Micronesia).  However the prior implementation never worked, and why the rule was configured is unknown.
 * There is no reference to either of those territories in NOFO or US Code for AFG.
 *
 * The computeCostShare() returns an object containing share and extras properties.  It will delegate to either
 * computeCostShareUsingConfiguredRates(), or computeCostShareUsingGivenAppShare() when there is an approved
 * waiver.  In the latter case, the costShareInfo property to the constructor along with the appShare property
 * will need to be supplied in order to determine their is an approved waiver make use of the supplied appShare
 * values as is and determine fedShare from that and the totals.
 *
 * The computeBudgetObj() function will return an object not only with share and extras property but with a
 * budgetSummary property as well.  Its intent is to be used in a new listener that will replace
 * BudgetSummaryListenerV2.
 *
 * It depends on CostItemObjectTotaler and BudgetCalcRulesEvaluator to do its job.
 * See "PrepFirebudgetCalculatgor Implementation" confluence page for more information...
 * Product > Fema GO Features > Technical Enablers > Cost Share and budget calculations refactor
 *   > Design Approach > PrepFire Budget Calculation Refactoring > PrepFireBudgetCalculatgorImplementation
 * https://maestro.dhs.gov/confluence/spaces/GMMGO/pages/1851754460/PrepFireBudgetCalculator+Implementation
 *
 * Arguments to PrepFireBudgetCalculator constructor
 * budgetCalcConfig - (required) the "budgetCalcConfig" property within the fundingOpportunityConfig.json config file.
 * totalerType - (required) "default", "actuals", or "allowables"
 * fundingStreams - the "fundingStreams" property in payload of entity being edited that contains all the cost items
 *   (Legacy listener was firing even before any cost items added, so expect new listener that uses this class
 *   to do the same.)
 * costShareInfo - awardRecommendation.payload.grantsManagement.costShareInformation contains fields used to
 *   determine whether or not there is an approved cost share waiver.
 * appShare - the "budget.share.appShare" property in the payload of entity being edited, which are used as-is
 *   if there is an approved cost share waiver.
 * appData - application data provides the context for which to interpret rules used by AFG config
 * fedShareAdjustment - grant-specific adjustments made to fedShare period amounts to force calculation
 *   to return same result as legacy calculation used initially for the grant. Only applies to grants
 *   w/o an approved cost share waiver.
 *
 * NOTE 02/26/2026: to start with, this class is generating "appRate" and "fedRate" array properties
 * found in budget.share as legacy did, to be backwards compatible with other code that makes use of those rates.
 * The requirement of the logic for this code is that separate appRate arrays are configured for each
 * fundingStream (FS) in a funding opportunity (FO), and that those rates may be different for each FS.
 * So persisting a single rate does not make sense, and at some point in the (near?) future either no
 * rates will be persisted in the payload or the structure for appRate becomes an object with keys
 * for each FS type and their associated appRates array.
 *
 * Without an approved cost share waiver, this calculator gets the rates it needs from
 * fundingOpportunityConfig.json budgetCalcConfig, and does not use rates that are persisted.
 * There is some existing code that uses the persisted rates.  Spike GMMGO-71931 identified areas
 * where this is the case, and tickets GMMGO-72077, GMMGO-72081, GMMGO-72082 came out of that.
 * But there is the possiblity what we want to deploy and toggle on use of this code
 * before those tickets are addressed.  So we are continuing to generate an appRate and
 * fedRate property in the budget.share object of the payload which is a single array of
 * rates.  That really doesn't make sense when there are rates defined per fundingStreamType,
 * and an application can have multiple fundingStreamTypes which possibly use different rate
 * values.  That currently is not the case for prepFire applications since the same rate is
 * used for AFG program (either 0.05, 0.10, or 0.15) for all fundingStreamTypes, as is the
 * case for FPS program (0.05 everywhere).  The SAFER program apps will either have a
 * single hiringOfFirefighters funding stream (appRate [0.25, 0.25, 0.65]), or a
 * recruitementAndRetention funding stream possibly with a preAwardCosts both of which
 * have appRate of 0 ([0, 0, 0, 0] and [0] respectively).
 */
class PrepFireBudgetCalculator {
  constructor(
    budgetCalcConfig,
    totalerType,
    fundingStreams,
    costShareInfo,
    appShare,
    appData,
    fedShareAdjustments,
    toggles
  ) {
    this.budgetCalcConfig = budgetCalcConfig;
    this.rulesEvaluator = new BudgetCalcRulesEvaluator(budgetCalcConfig);
    this.costItemObjectTotaler = new CostItemObjectTotaler(totalerType);
    this.totalerType = totalerType;
    this.fundingStreams = fundingStreams;
    this.costShareInfo = costShareInfo;
    this.appShare = appShare;
    this.appData = appData;
    this.fedShareAdjustments = fedShareAdjustments;
    this.toggles = toggles;

    if (this.rulesEvaluator.hasRules()) {
      const evaluatedRules = this.rulesEvaluator.evaluateRules(this.appData);
      this.budgetCalcConfig = this.rulesEvaluator.applyEvaluatedRules(evaluatedRules);
    }
    this.pennyPrecisionFunc = this.rulesEvaluator.getPennyPrecisionFunc();
    this.isNormalizedConfig = this.rulesEvaluator.isBudgetCalcConfigNormalized();

    this.loggingOn = false;
  }

  /**
   * Return true if costShareInfo.waiverDetermination is 'Approved' and
   * waiverRequested is either true or 'Yes'.  Otherwise return false.
   */
  haveApprovedCostShareWaiver() {
    if (this.costShareInfo) {
      const {waiverDetermination, waiverRequested} = this.costShareInfo;

      return (
        (waiverRequested === true || waiverRequested === 'Yes') &&
        waiverDetermination === 'Approved'
      );
    }
    return false;
  }

  /**
   * This delegates to method in BudgetCalcRulesEvaluator, to support
   * a rule in AFG that would flag an SFTA applicant from MH or FM
   * as being ineligible.
   * NOTE 02/26/2026: The legacy implementation never worked,
   * and it is not clear why the rule was ever implmented. No
   * reference to MH (Marshal Islands) or FM (Federated states of
   * Micronesia) are made in the NOFO, so were did requirement
   * come from?  Support for this configuration may get removed.
   * @returns true if applicant is eligible or false otherwise
   */
  isEligible() {
    return this.rulesEvaluator.isEligible();
  }

  /**
   * Collects all the numPeriods values for the fundingStreamType values appearing
   * in this.fundingStreams and returns the one with the max value.
   * @returns numPeriods value for the fundingStreamType in
   * fundingStreams whose value is the largest.
   */
  getMaxNumPeriodsInFundingStreams() {
    let numPeriodsArr = [];
    _.forEach(this.fundingStreams, (fs) => {
      let fsType = fs.fundingStreamType;
      let numPeriods = _.get(
        this.budgetCalcConfig,
        `fundingStreamConfigs[${fsType}].numPeriods`,
        0
      );
      numPeriodsArr.push(numPeriods);
    });
    return _.max(numPeriodsArr);
  }

  /**
   * Convert appRates to values we can multiply a total with.
   * If appRateBasedOn is "federal" then that is rate/(1+rate)
   * otherwise it is just the rate
   *
   * @param {*} appRates array of appRate values
   * @returns array with rates mapped to rate/(1+rate)
   * if appRateBasedOn == "federal",
   * otherwise returns same array
   */
  getEffectiveAppRates(appRates) {
    if (_.get(this.budgetCalcConfig, 'appRateBasedOn') === 'federal') {
      let effAppRates = _.map(appRates, (appRate) =>
        safeDivide(appRate, safeAddNoFixed(1, appRate))
      );
      return effAppRates;
    } else {
      return appRates;
    }
  }

  ensureTwoDecimalPlaces(cost) {
    let number = _.toNumber(cost); // ensure its a number. undefined, null, "" are 0.
    // bad input string such as "unparsableNumberString" result in NaN above.  Set those to 0.
    if (isNaN(number)) {
      number = 0;
    }
    return parseFloat(number.toFixed(2));
  }

  /**
   * Serves same purpose as safeAddReduce(valueList, base),
   * but uses safeAddNoFixed(m, n)
   * @returns sum of all the numbers in the nbrArr array
   */
  sumUpMoneyArray(nbrArr) {
    let result = _.reduce(nbrArr, (total, nbr) => safeAddNoFixed(total, nbr), 0);
    return this.ensureTwoDecimalPlaces(result);
  }

  buildBudgetObjWithIneligibleExtrasObj() {
    const budgetObj = {
      extras: {
        isEligible: false,
      },
    };
    const message = _.get(this.budgetCalcConfig, 'notEligibleMessage');
    if (message) {
      budgetObj.extras.message = message;
    }
    return budgetObj;
  }

  /**
   * Returns object with share and extras properties.
   * The extras defaults to {isEligible: true, message:{}},
   * but could contain a message if fedShare limit exceeded,
   * or ineligible applicant.
   *
   * The share object contains properties that are arrays whose
   * size will be the same as that funding stream in the entity
   * that has the largest numPeriods value.
   * o appShare - value for each period indicating applicant's share of period costs
   * o fedShare - value for each period indicating fed's share of period costs
   * o appRate - the appRates array from fundingStreamConfig.json budgetCalcConfig.fundingStreamConfigs,
   *     for funding stream with largest numPeriods
   * o fedRate - the appRates values mapped to 1-appRate
   * @returns object with a "share" object and "extras"
   */
  computeCostShareUsingConfiguredRates() {
    /*
     * If we don't have a normalized config, then we can't compute share.
     * If applicant is not eligible for grant, then we don't want to bother computing share.
     * Ditto for no or empty fundingStreams
     * Return empty object in these cases as an indication of such conditions.
     */
    if (
      !this.isNormalizedConfig ||
      !this.isEligible() ||
      _.isEmpty(this.fundingStreams)
    ) {
      return !this.isEligible() ? this.buildBudgetObjWithIneligibleExtrasObj() : {};
    }

    let extras = {isEligible: true, message: {}};

    /* Per-fundingStream processing...
     * Iterate through funding streams and get their total and get their raw appShare
     * Determine whether or not same appRate is used across all funding streams
     * and all their periods.
     *
     * Hold onto appRates array for funding stream (FS) type in entity that has
     * max number of periods.  This is what will be saved as budget.share.appRate property
     *   - For AFG that's any of the FS, all have numPeriods=1
     *   - For FPS that's either firePreventionAndSafety (2), or
     *     researchAndDevelopment (4)
     *   - For SAFER that's hiringOfFirefighters (3) which should be on
     *     its own, or recuitmentAndRetention (4)
     *
     * See NOTE at top of class regarding persisting of rates. This currently
     * returns appRate array from appRates in budgetCalcConfig for funding stream
     * w/ max numPeriods value for funding stream that exists in this.fundingStreams.
     * The fedRate is just map of appRate array using (rate) => 1-rate.
     */
    const maxNumPeriods = this.getMaxNumPeriodsInFundingStreams();
    this.loggingOn &&
      console.log(
        `computeCostShareUsingConfiguredRates(): Iterate through funding streams to get their total and rawAppShare, (maxNumPeriods=${maxNumPeriods})`
      );
    let appRatesForMaxPeriodFS;
    let appRatesByFundingStreams = {};
    const totalsByFundingStreamByPeriod = {};
    const rawAppShareByFundingStreamByPeriod = {};
    let singleAppRateUsedEverywhere = true;
    let theSingleConfiguredAppRate;

    _.forEach(this.fundingStreams, (fs) => {
      let fsType = fs.fundingStreamType;
      let fsAppRates = this.budgetCalcConfig.fundingStreamConfigs[fsType].appRates;
      let fsNumPeriods = this.budgetCalcConfig.fundingStreamConfigs[fsType].numPeriods;
      appRatesByFundingStreams[fsType] = [...fsAppRates];
      if (fsNumPeriods == maxNumPeriods) {
        appRatesForMaxPeriodFS = [...fsAppRates];
      }
      let fsEffectiveAppRates = this.getEffectiveAppRates(fsAppRates);
      let totalerTypeOverride = _.get(
        this.budgetCalcConfig,
        `fundingStreamConfigs[${fsType}].totalerTypeOverride[${this.totalerType}]`
      );
      let totaler = totalerTypeOverride
        ? new CostItemObjectTotaler(totalerTypeOverride)
        : this.costItemObjectTotaler;

      let totalCostsPerPeriod = totaler.sumCostItemObjectsInTree(maxNumPeriods, fs); // fs totals
      totalsByFundingStreamByPeriod[fsType] = _.map(totalCostsPerPeriod, (cost) =>
        this.ensureTwoDecimalPlaces(cost)
      );
      rawAppShareByFundingStreamByPeriod[fsType] = [];

      /* compute raw appShare for each period
       * (for fundStreams w/ numPeriods < max, should have 0 total so 0 appShare)
       */
      for (let p = 0; p < maxNumPeriods; p++) {
        let periodAppRate = _.get(fsEffectiveAppRates, p, 1); // configured appRates.length may be < maxNumPeriods
        let periodTotal = totalCostsPerPeriod[p]; // but totals beyond numPeriods for this fs will be 0
        let periodAppShare = safeMult(periodAppRate, periodTotal);
        rawAppShareByFundingStreamByPeriod[fsType].push(periodAppShare);

        if (singleAppRateUsedEverywhere) {
          // if we already determined it is not, then don't bother
          if (theSingleConfiguredAppRate === undefined)
            theSingleConfiguredAppRate = fsAppRates[p];
          /* if this funding stream has a rate for this period and it is not equal to THE single app rate, unset the flag
           * e.g., SAFER hiringOfFirefighters which has appRates [.25, .25, .65]
           */
          if (fsAppRates[p] && fsAppRates[p] != theSingleConfiguredAppRate)
            singleAppRateUsedEverywhere = false;
        }
      }
      if (this.loggingOn) {
        console.log(
          `${fsType}: period totals: ${JSON.stringify(
            totalsByFundingStreamByPeriod[fsType]
          )}\n` +
            `${fsType}: period raw appShares: ${JSON.stringify(
              rawAppShareByFundingStreamByPeriod[fsType]
            )}`
        );
      }
    });

    /* Total up across funding streams to get single array of period totals,
     * and single array of raw appShare values per period.
     */
    const totalsByPeriod = [];
    const rawAppShareByPeriod = [];
    // initialize period amounts to 0
    for (let p = 0; p < maxNumPeriods; p++) {
      totalsByPeriod[p] = 0;
      rawAppShareByPeriod[p] = 0;
    }
    for (let fsType in totalsByFundingStreamByPeriod) {
      let fsPeriodTotals = totalsByFundingStreamByPeriod[fsType];
      let fsPeriodRawAppShares = rawAppShareByFundingStreamByPeriod[fsType];
      for (let p = 0; p < maxNumPeriods; p++) {
        let fsPeriodTotal = _.get(fsPeriodTotals, `[${p}]`, 0);
        totalsByPeriod[p] = safeAddNoFixed(totalsByPeriod[p], fsPeriodTotal);

        let fsPeriodRawAppShare = _.get(fsPeriodRawAppShares, `[${p}]`, 0);
        rawAppShareByPeriod[p] = safeAddNoFixed(
          rawAppShareByPeriod[p],
          fsPeriodRawAppShare
        );
      }
    }
    let totalProjectCost = this.sumUpMoneyArray(totalsByPeriod); // aka TPC
    let appShareByPeriod = _.map(rawAppShareByPeriod, (rawAppShare) =>
      this.pennyPrecisionFunc(rawAppShare)
    );
    let appShareTotal = this.sumUpMoneyArray(appShareByPeriod);

    if (this.loggingOn) {
      console.log(
        ` totalsByPeriod = ${JSON.stringify(totalsByPeriod)}\n` +
          `totalProjectCost = ${totalProjectCost}\n\n` +
          `rawAppShareByPeriod = ${JSON.stringify(rawAppShareByPeriod)}\n` +
          `   appShareByPeriod = ${JSON.stringify(appShareByPeriod)}\n` +
          `      appShareTotal = ${appShareTotal}`
      );
    }

    /* Reconcile with total...
     * Assumption specified in GMMGO-67661 epic:
     * If same appRate used everywhere for entity, apply that rate to total
     * and reconcile period values so they total up to that value.
     */
    if (singleAppRateUsedEverywhere) {
      let theSingleEffectiveAppRate = theSingleConfiguredAppRate;
      if (this.budgetCalcConfig.appRateBasedOn === 'federal') {
        theSingleEffectiveAppRate = safeDivide(
          theSingleConfiguredAppRate,
          safeAddNoFixed(1, theSingleConfiguredAppRate)
        );
      }
      let rawAppShareFromTotal = safeMult(totalProjectCost, theSingleEffectiveAppRate);
      let expectedAppShareTotal = this.pennyPrecisionFunc(rawAppShareFromTotal);
      if (appShareTotal != expectedAppShareTotal) {
        // Adjust the last period's appShare by difference between calculated and expected
        let reconciliationAmount = safeSubstractNoFixed(
          expectedAppShareTotal,
          appShareTotal
        );
        let lastPeriodIdx = maxNumPeriods - 1;
        if (this.loggingOn) {
          console.log(
            `Reconcile with total ${totalProjectCost}...\n` +
              `Expected appShareTotal ${expectedAppShareTotal} =  pennyFunc(${totalProjectCost}*${theSingleEffectiveAppRate})\n` +
              `  Actual appShareTotal ${appShareTotal}.\n` +
              `Difference (reconciliationAmount), ${reconciliationAmount}, to be added to last period amount (${appShareByPeriod[lastPeriodIdx]}).`
          );
        }
        let lastPeriodAppShare = safeAddNoFixed(
          appShareByPeriod[lastPeriodIdx],
          reconciliationAmount
        );
        appShareByPeriod[lastPeriodIdx] = this.ensureTwoDecimalPlaces(lastPeriodAppShare);
        this.loggingOn &&
          console.log(
            `appShareByPeriod[lastPeriodIdx] = ${appShareByPeriod[lastPeriodIdx]}`
          );
      }
    }

    // obtain period fedShare by subtracting period appShare from period total
    let fedShareByPeriod = _.map(totalsByPeriod, (periodTotal, p) => {
      let periodFedShare = safeSubstractNoFixed(periodTotal, appShareByPeriod[p]);
      return this.ensureTwoDecimalPlaces(periodFedShare);
    });
    let fedShareTotal = this.sumUpMoneyArray(fedShareByPeriod);
    if (this.loggingOn) {
      console.log(
        `fedShareByPeriod=${JSON.stringify(fedShareByPeriod)}\n` +
          `   fedShareTotal=${fedShareTotal}`
      );
    }

    /*
     * If fedShareLimit defined and exceeded, then apply excess to appShare and
     * reduce fedShare down to limit
     */
    const fedShareLimit = this.budgetCalcConfig.fedShareLimit;
    const fedShareLimitMsg = this.budgetCalcConfig.fedShareLimitMessage;
    if (fedShareLimit && fedShareTotal > fedShareLimit) {
      let adjustedAppShare = [...appShareByPeriod];
      let adjustedFedShare = [...fedShareByPeriod];
      let remainingExcess = safeSubstract(fedShareTotal, fedShareLimit);
      this.loggingOn &&
        console.log(
          `fedShareLimit of ${fedShareLimit} exceeded by ${remainingExcess}, adjusting...`
        );
      for (let p = maxNumPeriods - 1; p >= 0; p--) {
        let periodAppShare = adjustedAppShare[p];
        let periodFedShare = adjustedFedShare[p];
        if (remainingExcess > 0) {
          if (remainingExcess > periodFedShare) {
            adjustedAppShare[p] = safeAdd(periodAppShare, periodFedShare);
            adjustedFedShare[p] = 0;
            remainingExcess = safeSubstract(remainingExcess, periodFedShare);
          } else {
            adjustedAppShare[p] = safeAdd(periodAppShare, remainingExcess);
            adjustedFedShare[p] = safeSubstract(periodFedShare, remainingExcess);
            remainingExcess = 0;
          }
        } else {
          break;
        }
      }
      if (fedShareLimitMsg) {
        extras.message = {...fedShareLimitMsg};
      }
      if (this.loggingOn) {
        let fedShare = this.sumUpMoneyArray(adjustedFedShare);
        console.log(
          `appShareByPeriod: ${JSON.stringify(appShareByPeriod)}\n` +
            `adjustedAppShare: ${JSON.stringify(adjustedAppShare)}\n\n` +
            `fedShareByPeriod: ${JSON.stringify(fedShareByPeriod)}\n` +
            `adjustedFedShare: ${JSON.stringify(adjustedFedShare)}\n` +
            `total fedShare = ${fedShare}`
        );
      }

      appShareByPeriod = adjustedAppShare;
      fedShareByPeriod = adjustedFedShare;
    }

    /*
     * Grant-specific adjustments are defined in awardRecommendation payload
     * under budget.extras.fedShareAdjustments which is an array of values,
     * one for each period that will be added to the fedShare and
     * subtracted from the appShare.
     */
    if (_.isArray(this.fedShareAdjustments)) {
      this.loggingOn &&
        console.log(
          `Adjust period values using fedShareAdjustments provided: ${JSON.stringify(
            this.fedShareAdjustments
          )}`
        );
      for (let p = 0; p < maxNumPeriods && p < this.fedShareAdjustments.length; p++) {
        let periodFedAdjustment = this.fedShareAdjustments[p];
        if (periodFedAdjustment == 0) {
          this.loggingOn && console.log(`  Period ${p}: No adjustment.`);
          continue;
        }
        let periodFedShare = fedShareByPeriod[p];
        let periodAppShare = appShareByPeriod[p];
        this.loggingOn &&
          console.log(
            `  Period ${p}: add ${periodFedAdjustment} to fedShare of ${periodFedShare},` +
              ` and subtract ${periodFedAdjustment} from appShare of ${periodAppShare}`
          );
        fedShareByPeriod[p] = safeAdd(fedShareByPeriod[p], this.fedShareAdjustments[p]);
        appShareByPeriod[p] = safeSubstract(
          appShareByPeriod[p],
          this.fedShareAdjustments[p]
        );
      }
    }

    let fedRates = _.map(appRatesForMaxPeriodFS, (appRate) => 1 - appRate);
    let fedRatesByFundingStreams = _.mapValues(appRatesByFundingStreams, (appRatesArr) =>
      appRatesArr.map((appRate) => 1 - appRate)
    );

    /* Final set of code to ensure 2-digit precision before return
     */
    let appShare = _.map(appShareByPeriod, (cost) => parseFloat(cost.toFixed(2)));
    let fedShare = _.map(fedShareByPeriod, (cost) => parseFloat(cost.toFixed(2)));
    let totalShare = _.map(totalsByPeriod, (cost) => parseFloat(cost.toFixed(2)));
    let share;
    if (_.includes(this.toggles, 'persistedAppFedRateFormatChange')) {
      share = {
        appShare,
        fedShare,
        totalShare,
        appRate: appRatesByFundingStreams,
        fedRate: fedRatesByFundingStreams,
      };
    } else {
      share = {
        appShare,
        fedShare,
        totalShare,
        appRate: appRatesForMaxPeriodFS,
        fedRate: fedRates,
      };
    }

    return {share, extras, total: totalProjectCost, numPeriods: maxNumPeriods};
  }

  /*
   * 2026-05-07: For approved cost share waiver, we are keeping format of rate arrays
   * to the single array format for appRate, fedRate, which differs from format
   * used when calculating share from configured rates which was changed to save
   * app/fedRate as an object with keys for each fundingStreamType present and the
   * configured rate for that fundingStreamType.  For approved waivers, you are
   * not calculating appShare via rates, app share is entered directly
   * (and in prod had been left at $0).  GMMGO-72962 "Update prepfire calculator
   * to save new rate format" is also related to GMMGO-71932, GMMGO-72077, GMMGO-72081,
   * and GMMGO-72082 are related to this ticket for code that referenced persisted
   * rates. We are either getting rid of the reference (e.g., display of rate in template)
   * or handling both formats to use array if it is array or object if it is an
   * object.
   */
  computeCostShareUsingGivenAppShare() {
    if (
      !this.isNormalizedConfig ||
      !this.isEligible() ||
      _.isEmpty(this.fundingStreams) ||
      _.isEmpty(this.appShare)
    ) {
      return !this.isEligible() ? this.buildBudgetObjWithIneligibleExtrasObj() : {};
    }
    let share = {
      appShare: [],
      fedShare: [],
      appRate: [],
      fedRate: [],
    };
    let extras = {isEligible: true, message: {}};

    const maxNumPeriods = this.getMaxNumPeriodsInFundingStreams();
    const totalsByFundingStreamByPeriod = {};

    _.forEach(this.fundingStreams, (fs) => {
      let fsType = fs.fundingStreamType;
      let totalerTypeOverride = _.get(
        this.budgetCalcConfig,
        `fundingStreamConfigs[${fsType}].totalerTypeOverride[${this.totalerType}]`
      );
      let totaler = totalerTypeOverride
        ? new CostItemObjectTotaler(totalerTypeOverride)
        : this.costItemObjectTotaler;
      let totalCostsPerPeriod = totaler.sumCostItemObjectsInTree(maxNumPeriods, fs); // fs totals
      totalsByFundingStreamByPeriod[fsType] = totalCostsPerPeriod;
    });
    this.loggingOn &&
      console.log(
        `totalsByFundingStreamByPeriod=${JSON.stringify(
          totalsByFundingStreamByPeriod,
          null,
          2
        )}`
      );
    // Total up across funding streams to get single array of period totals
    const totalsByPeriod = [];
    // initialize period amounts to 0
    for (let p = 0; p < maxNumPeriods; p++) {
      totalsByPeriod[p] = 0;
    }
    for (let fsType in totalsByFundingStreamByPeriod) {
      let fsPeriodTotals = totalsByFundingStreamByPeriod[fsType];
      for (let p = 0; p < maxNumPeriods; p++) {
        let fsPeriodTotal = _.get(fsPeriodTotals, `[${p}]`, 0);
        totalsByPeriod[p] = safeAddNoFixed(totalsByPeriod[p], fsPeriodTotal);
      }
    }
    this.loggingOn &&
      console.log(`totalsByPeriod=${JSON.stringify(totalsByPeriod, null, 2)}`);

    _.forEach(totalsByPeriod, (periodTotal, p) => {
      let periodAppShare = _.get(this.appShare, `[${p}]`, 0);
      let periodFedShare = safeSubstractNoFixed(periodTotal, periodAppShare);
      /* Rate calculations:
       * Rate to be saved is should be one equivalent to configured rate,
       * which may need convertion to effective appRate if appRateBasedO
       * equals "federal" (or derivedAppRate flag is true).
       *
       * The code that is using the persisted appRate we are generating here,
       * needs a value consistent with a configured appRate before it
       * has a (appRate/(1+appRate)) conversion to effectiveAppRate.
       *
       * The RequestDetailsControllerV3.saveToStore(updateData, fieldName)
       * method and its use of utils.js'
       * calculateBudget(..., fedShareRate, appShareRate, ...)
       * is performing the appRate/(1+appRate) conversion to effectiveAppRate
       * using values persisted in payload.
       *
       * See NOTE at top of class regarding rates.
       */
      let periodAppRate = safeDivide(periodAppShare, periodTotal);
      let periodFedRate = safeSubstractNoFixed(1, periodAppRate);

      share.appShare.push(this.ensureTwoDecimalPlaces(periodAppShare));
      share.fedShare.push(this.ensureTwoDecimalPlaces(periodFedShare));
      share.appRate.push(periodAppRate);
      share.fedRate.push(periodFedRate);
    });

    return {
      share,
      extras,
    };
  }

  /**
   * Compute total of all funding stream cost items for each period
   */
  computeCostTotal() {
    const maxNumPeriods = this.getMaxNumPeriodsInFundingStreams();
    const totalsByPeriod = _.fill(Array(maxNumPeriods), 0);

    _.forEach(this.fundingStreams, (fs) => {
      let fsType = fs.fundingStreamType;
      let totalerTypeOverride = _.get(
        this.budgetCalcConfig,
        `fundingStreamConfigs[${fsType}].totalerTypeOverride[${this.totalerType}]`
      );
      let totaler = totalerTypeOverride
        ? new CostItemObjectTotaler(totalerTypeOverride)
        : this.costItemObjectTotaler;

      let fsPeriodTotals = totaler.sumCostItemObjectsInTree(maxNumPeriods, fs); // fs totals
      for (let p = 0; p < maxNumPeriods; p++) {
        let fsPeriodTotal = _.get(fsPeriodTotals, `[${p}]`, 0);
        totalsByPeriod[p] = safeAddNoFixed(totalsByPeriod[p], fsPeriodTotal);
      }
    });

    let totalProjectCost = this.sumUpMoneyArray(totalsByPeriod);

    let share = {
      totalShare: totalsByPeriod,
    };
    return {share, total: totalProjectCost, numPeriods: maxNumPeriods};
  }

  /**
   * Delegates to either computeCostShareUsingGivenAppShare(),
   * or computeCostShareUsingConfiguredRates() depending on if
   * there is an approved cost share waiver or not.
   * Both return an object with a share and extras property.
   * @returns object w/ "share" and "extras" properties
   */
  computeCostShare() {
    if (this.haveApprovedCostShareWaiver()) {
      return this.computeCostShareUsingGivenAppShare();
    } else {
      // return this.computeCostShareUsingConfiguredRates();
      let o = this.computeCostShareUsingConfiguredRates();
      return o;
    }
  }

  /**
   * @return a "budget" object that contains the
   * "share", "extras", and "budgetSummary" properties
   */
  computeBudgetObj() {
    /*
     * First two conditions concern AFG applications.  If user hasn't sepecified
     * what type of org they are and either what state/territory they are from (if SFTA applicant),
     * or permanent resident population (non-SFTA applicant), then can't determine the rate
     * so non-normalized config, and w/o rate can't determine app share.  AFG also traditioinally
     * had a rule to flag MH and FM territory SFTA applicants as ineligible.  If ineligible,
     * don't bother calculating budget and return configured message if one exists.
     *
     * If you don't have fundingStreams then there is no cost input from which to determine
     * applicant and federal share of such costs.
     */
    if (
      !this.isNormalizedConfig ||
      !this.isEligible() ||
      _.isEmpty(this.fundingStreams)
    ) {
      return !this.isEligible() ? this.buildBudgetObjWithIneligibleExtrasObj() : {};
    }

    let extrasAndShareObj = this.computeCostShare();
    let maxNumPeriods = this.getMaxNumPeriodsInFundingStreams();
    let budgetSummary = this.costItemObjectTotaler.buildBudgetSummaryObj(
      maxNumPeriods,
      this.fundingStreams
    );

    let budget = {
      ...extrasAndShareObj,
      budgetSummary,
    };
    return budget;
  }
}

export default PrepFireBudgetCalculator;
